import { useCallback, useEffect, useMemo, useState } from "react";
import { FormikTouched } from "formik";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import {
  Package,
  PackageOffering,
  PackagesData,
} from "../../Center/CenterSettingsContainer/types";
import { useUIContext } from "../../Unknown/UIContext";
import { CenterPackageFormValues } from "../CenterPackageForm/types";
import { CenterPackageFormikProps } from "../CenterPackageFormik";
import useTranslations from "./useTranslations";

type EditFormHookParams = {
  isOpened: boolean;
  editedPackageId: string | null;
  onClose: () => void;
  packagesData: PackagesData;
  refetchCenterData?: () => void | Promise<void>;
};

const useEditForm = (
  params: EditFormHookParams,
): CenterPackageFormikProps | null => {
  const {
    isOpened,
    editedPackageId,
    onClose,
    packagesData,
    refetchCenterData,
  } = params;

  const { packages, minPrice, currencyCode, offerings } = packagesData;

  const updateCenterPackage = useFirebaseAppFunction("updateCenterPackage");

  const { editModalTitle, defaultError } = useTranslations();

  const { setAlert } = useUIContext();

  const [centerPackageData, setCenterPackageData] = useState<Package | null>(
    null,
  );

  useEffect(() => {
    if (!isOpened) return;

    if (!editedPackageId) {
      setCenterPackageData(null);
      onClose();
      return;
    }

    const selectedPackage = packages.find(
      (packageItem) => packageItem.id === editedPackageId,
    );

    if (!selectedPackage) {
      setCenterPackageData(null);
      onClose();
      return;
    }

    setCenterPackageData(selectedPackage);
  }, [editedPackageId, isOpened, packages, onClose]);

  const initialValues = useMemo<CenterPackageFormValues | null>(() => {
    if (!centerPackageData) return null;

    return {
      name: centerPackageData.name,
      modalityName: centerPackageData.modalityName,
      description: centerPackageData.description,
      price: centerPackageData.price,
      depositAmount: centerPackageData.depositAmount,
      visibility: centerPackageData.visibility,
      marketingText: centerPackageData.marketingText,
      durationMinutes: centerPackageData.durationMinutes || "",
      offerings: centerPackageData.offerings.map((offering) => ({
        id: offering.id,
        name: offering.name,
        price: offering.price,
        durationMinutes: offering.durationMinutes,
      })),
    };
  }, [centerPackageData]);

  const initialTouched: FormikTouched<CenterPackageFormValues> = {
    price: true,
  };

  const handleSubmit = useCallback(
    async (values: CenterPackageFormValues) => {
      try {
        if (!centerPackageData) throw new Error(defaultError);

        const {
          name,
          modalityName,
          description,
          offerings: selectedOfferings,
          visibility,
          price,
          depositAmount,
          marketingText,
          durationMinutes,
        } = values;

        if (visibility === "" || depositAmount === "") {
          throw new Error(defaultError);
        }

        const offeringIds = selectedOfferings.map(({ id }) => id);

        const offeringsDuration = selectedOfferings.reduce((acc, offering) => {
          return acc + offering.durationMinutes;
        }, 0);

        const offeringsPrice = selectedOfferings.reduce((acc, offering) => {
          return acc + offering.price;
        }, 0);

        await updateCenterPackage({
          id: centerPackageData.id,
          name,
          modalityName,
          description,
          offeringIds,
          visibility,
          price: price || offeringsPrice,
          depositAmount,
          marketingText,
          durationMinutes: durationMinutes || offeringsDuration,
        });

        if (refetchCenterData) await refetchCenterData();

        onClose();
      } catch (error) {
        setAlert({
          message: defaultError,
          isShown: true,
          severity: "error",
        });
      }
    },
    [
      centerPackageData,
      defaultError,
      onClose,
      refetchCenterData,
      setAlert,
      updateCenterPackage,
    ],
  );

  let allOfferings: PackageOffering[] = [...offerings].map(
    (packageDataOffering) => {
      return {
        id: packageDataOffering.id,
        name: packageDataOffering.name,
        price: packageDataOffering.price,
        side: packageDataOffering.scan_side,
        visibility: packageDataOffering.visibility,
        durationMinutes: packageDataOffering.duration_minutes,
      };
    },
  );

  const centerPackageOfferings =
    packages.find(({ id }) => id === editedPackageId)?.offerings || [];

  centerPackageOfferings.forEach((packageOffering) => {
    const isAlreadyExist = offerings.some(
      (offering) => offering.id === packageOffering.id,
    );

    if (!isAlreadyExist) {
      allOfferings = [packageOffering, ...allOfferings];
    }
  });

  if (!initialValues) return null;

  return {
    title: editModalTitle,
    isOpened,
    onModalClose: onClose,
    initialValues,
    initialTouched,
    handleSubmit,
    currencyCode,
    minPrice,
    offerings: allOfferings,
  };
};

export default useEditForm;
